import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getSavedName, saveName } from '@/lib/visitor';
import { trackEvent } from '@/lib/analytics';

interface Comment {
  id: string;
  body: string;
  author_name: string | null;
  created_at: string;
}

const IdeaComments = ({ ideaId, onAdded }: { ideaId: string; onAdded?: () => void }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [body, setBody] = useState('');
  const [name, setName] = useState(getSavedName());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    supabase
      .from('idea_comments')
      .select('id,body,author_name,created_at')
      .eq('idea_id', ideaId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (active) setComments((data as Comment[]) ?? []);
      });
    return () => { active = false; };
  }, [ideaId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    setLoading(true);
    saveName(name);
    const { data, error } = await supabase
      .from('idea_comments')
      .insert({ idea_id: ideaId, body: trimmed.slice(0, 500), author_name: name.trim() || null })
      .select('id,body,author_name,created_at')
      .single();
    if (!error && data) {
      setComments(prev => [...prev, data as Comment]);
      setBody('');
      trackEvent('comment_posted', { ideaId });
      onAdded?.();
    }
    setLoading(false);
  };

  return (
    <div className="mt-4 border-t border-border pt-4">
      {comments.length > 0 && (
        <ul className="space-y-3 mb-4">
          {comments.map(c => (
            <li key={c.id} className="font-mono text-sm">
              <span className="text-primary">{c.author_name || 'Anonymous'}</span>
              <span className="text-muted-foreground"> · {new Date(c.created_at).toLocaleDateString()}</span>
              <p className="text-foreground/90 mt-1 whitespace-pre-wrap break-words">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={40}
          placeholder="Name"
          aria-label="Your name, optional"
          className="sm:w-32 h-10 px-3 rounded bg-background/60 border border-border font-mono text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
        />
        <input
          value={body}
          onChange={e => setBody(e.target.value)}
          maxLength={500}
          placeholder="Add a comment..."
          aria-label="Your comment"
          className="flex-1 h-10 px-3 rounded bg-background/60 border border-border font-mono text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
        />
        <button
          type="submit"
          disabled={loading}
          className="h-10 px-5 rounded bg-card border border-border text-foreground font-bebas text-base tracking-wide hover:border-primary/50 disabled:opacity-50 transition-colors"
        >
          {loading ? '...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default IdeaComments;
